import { ApolloError } from "@apollo/client";
import { Add } from "@mui/icons-material";
import { Alert, Button, Dialog, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { SelectElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";

import { AutocompleteModel } from "~/__generated__";
import useAutocompleteGetOptions from "~/modules/app/api/useAutocompleteGetOptions";
import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import useRequireCurrentProject from "~/modules/project/hooks/useRequireCurrentProject";

type Props = {
    onAddProtocolHandler: (protocolId: string) => Promise<void>;
    addProtocolError?: ApolloError;
};

interface FormValues {
    protocolId: string;
}

export function AddProtocolToPlannerAreaModal({ onAddProtocolHandler, addProtocolError }: Props) {
    const { t } = useTranslation("mapEditor");
    const { project } = useRequireCurrentProject();

    const { data } = useAutocompleteGetOptions({
        model: AutocompleteModel.Protocol,
        params: { projectId: project.id },
    });

    const [open, setOpen] = useState(false);

    const {
        control,
        formState: { errors },
        handleSubmit,
        setError,
    } = useForm<FormValues>({
        values: { protocolId: "" },
    });

    const onSubmit = async (values: FormValues) => {
        await onAddProtocolHandler(values.protocolId);
        setOpen(false);
    };

    useApplyErrorToForm(addProtocolError, setError);

    return (
        <>
            <Button startIcon={<Add />} onClick={() => setOpen(true)} fullWidth variant="text">
                {t("featureDetailAreaSettings.btnAddProtocol")}
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{t("featureDetailAreaSettings.modal.title")}</DialogTitle>
                <DialogContent>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Stack gap={2}>
                            {errors.root && (
                                <Alert severity="error">
                                    <Typography>{errors.root.message}</Typography>
                                </Alert>
                            )}

                            <SelectElement
                                control={control}
                                label={t("featureDetailAreaSettings.modal.labelTitle")}
                                rules={{ required: true }}
                                name="protocolId"
                                options={data}
                                valueKey="value"
                            />
                            <GaiaHookFormSubmitButton control={control}>
                                {t("featureDetailAreaSettings.modal.btnSubmit")}
                            </GaiaHookFormSubmitButton>
                        </Stack>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
